import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="landing-page-customizer"
export default class extends Controller {
  static targets = [
    "tabButton", "tabContent", "importFileInput", "importForm",
    "primaryColor", "secondaryColor", "accentColor",
    "primaryColorText", "secondaryColorText", "accentColorText",
    "primaryPreview", "secondaryPreview", "accentPreview",
    "buttonPrimaryPreview", "buttonSecondaryPreview",
    "colorPresets"
  ]

  connect() {
    this.initializeTabs()
    this.setupColorPickers()
    this.setupImagePreviews()
    this.setupImportHandler()
  }

  // Setup import file change handler
  setupImportHandler() {
    if (this.hasImportFileInputTarget) {
      this.importFileInputTarget.addEventListener('change', () => {
        if (this.importFileInputTarget.files.length > 0) {
          this.submitImport()
        }
      })
    }
  }

  // Apply a color preset
  applyColorPreset(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const primaryColor = button.dataset.primary
    const secondaryColor = button.dataset.secondary
    const accentColor = button.dataset.accent

    // Use Stimulus targets for color pickers (defined in the view with data-landing-page-customizer-target)
    if (this.hasPrimaryColorTarget) {
      this.primaryColorTarget.value = primaryColor
      this.primaryColorTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }
    if (this.hasSecondaryColorTarget) {
      this.secondaryColorTarget.value = secondaryColor
      this.secondaryColorTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }
    if (this.hasAccentColorTarget) {
      this.accentColorTarget.value = accentColor
      this.accentColorTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    // Use Stimulus targets for text inputs
    if (this.hasPrimaryColorTextTarget) {
      this.primaryColorTextTarget.value = primaryColor
      this.primaryColorTextTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }
    if (this.hasSecondaryColorTextTarget) {
      this.secondaryColorTextTarget.value = secondaryColor
      this.secondaryColorTextTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }
    if (this.hasAccentColorTextTarget) {
      this.accentColorTextTarget.value = accentColor
      this.accentColorTextTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    // Update preview
    this.updateColorPreview()

    // Visual feedback - flash the preset button
    button.classList.add('ring-2', 'ring-fitcloud-coral', 'ring-offset-2')
    setTimeout(() => {
      button.classList.remove('ring-2', 'ring-fitcloud-coral', 'ring-offset-2')
    }, 500)
  }

  // Trigger the hidden file input for import
  triggerImport(event) {
    event.preventDefault()
    if (this.hasImportFileInputTarget) {
      this.importFileInputTarget.click()
    }
  }

  // Submit the import form
  submitImport() {
    if (this.hasImportFormTarget) {
      this.importFormTarget.submit()
    }
  }

  // Update color preview in real-time
  updateColorPreview() {
    // Get color values from Stimulus targets (color pickers)
    const primaryColor = this.hasPrimaryColorTarget ? this.primaryColorTarget.value : '#1e3a5f'
    const secondaryColor = this.hasSecondaryColorTarget ? this.secondaryColorTarget.value : '#f5f7fb'
    const accentColor = this.hasAccentColorTarget ? this.accentColorTarget.value : '#0f8b8d'

    // Update preview bars
    if (this.hasPrimaryPreviewTarget) {
      this.primaryPreviewTarget.style.backgroundColor = primaryColor
    }
    if (this.hasSecondaryPreviewTarget) {
      this.secondaryPreviewTarget.style.backgroundColor = secondaryColor
    }
    if (this.hasAccentPreviewTarget) {
      this.accentPreviewTarget.style.backgroundColor = accentColor
    }

    // Update preview buttons
    if (this.hasButtonPrimaryPreviewTarget) {
      this.buttonPrimaryPreviewTarget.style.backgroundColor = primaryColor
    }
    if (this.hasButtonSecondaryPreviewTarget) {
      this.buttonSecondaryPreviewTarget.style.borderColor = secondaryColor
      this.buttonSecondaryPreviewTarget.style.color = secondaryColor
    }
  }

  initializeTabs() {
    // Show the first tab by default
    const firstButton = this.element.querySelector('.tab-button')
    if (firstButton) {
      this.activateTab(firstButton.dataset.tab)
    }
  }

  switchTab(event) {
    event.preventDefault()
    const tabName = event.currentTarget.dataset.tab
    this.activateTab(tabName)
  }

  activateTab(tabName) {
    // Update button states (single token state class; styling lives in
    // owner_shell.css .owner-tab / .owner-tab--active)
    const buttons = this.element.querySelectorAll('.tab-button')

    buttons.forEach(btn => {
      btn.classList.toggle('owner-tab--active', btn.dataset.tab === tabName)
    })

    // Update tab content visibility
    const tabContents = this.element.querySelectorAll('.tab-content')

    tabContents.forEach(content => {
      content.classList.add('hidden')
    })

    const targetTab = document.getElementById(`${tabName}-tab`)
    if (targetTab) {
      targetTab.classList.remove('hidden')
    }
  }

  setupColorPickers() {
    // Sync color picker with text input
    this.element.querySelectorAll('input[type="color"]').forEach(colorPicker => {
      const textInput = colorPicker.nextElementSibling
      if (textInput && textInput.type === 'text') {
        colorPicker.addEventListener('input', (e) => {
          textInput.value = e.target.value
        })
        textInput.addEventListener('input', (e) => {
          if (/^#[0-9A-F]{6}$/i.test(e.target.value)) {
            colorPicker.value = e.target.value
          }
        })
      }
    })
  }

  setupImagePreviews() {
    // Setup preview for file inputs
    this.element.querySelectorAll('input[type="file"]').forEach(input => {
      if (input.accept && input.accept.includes('image')) {
        input.addEventListener('change', (e) => this.previewImage(e))
      }
    })
  }

  previewImage(event) {
    const input = event.target
    const file = input.files[0]

    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader()

      reader.onload = (e) => {
        // Find or create preview container
        let previewContainer = input.parentElement.querySelector('.image-preview')

        if (!previewContainer) {
          previewContainer = document.createElement('div')
          previewContainer.className = 'image-preview mt-2 relative inline-block'
          input.parentElement.appendChild(previewContainer)
        }

        previewContainer.innerHTML = `
          <img src="${e.target.result}" class="h-32 w-auto rounded-lg border-2 border-fitcloud-coral shadow-lg" alt="Preview">
          <span class="absolute top-1 right-1 bg-fitcloud-coral text-white text-xs px-2 py-1 rounded font-medium">New Image</span>
        `
      }

      reader.readAsDataURL(file)
    }
  }
}
